import { gsap } from './gsapSetup'

export interface HeroTimelineParts {
  scope: HTMLElement
  reduced: boolean
}

const q = (scope: HTMLElement, sel: string) => Array.from(scope.querySelectorAll<HTMLElement>(sel))

/**
 * Base full-bleed hero entrance — everything EXCEPT the heading (which needs font metrics and is
 * revealed separately after fonts load). Runs synchronously so nothing paints visible-then-hides.
 *
 * The LCP background image is animated transform-only (scale, NEVER opacity) so it stays an LCP
 * candidate and paints immediately. Reduced motion sets all elements visible with no transforms.
 */
export function heroIntro({ scope, reduced }: HeroTimelineParts): gsap.core.Timeline {
  const image = q(scope, '[data-hero="image"]')
  const scrim = q(scope, '[data-hero="scrim"]')
  const eyebrow = q(scope, '[data-hero="eyebrow"]')
  const lead = q(scope, '[data-hero="lead"]')
  const cta = q(scope, '[data-hero="cta"] > *')
  const meta = q(scope, '[data-hero="meta"] > *')
  const cue = q(scope, '[data-hero="cue"]')

  const tl = gsap.timeline({ defaults: { ease: 'power4.out' } })

  if (reduced) {
    gsap.set([...scrim, ...eyebrow, ...lead, ...cta, ...meta, ...cue], { opacity: 1, clearProps: 'transform' })
    gsap.set(image, { opacity: 1, scale: 1 })
    return tl
  }

  gsap.set(image, { transformOrigin: '60% 50%' })

  // transform-only ken-burns keeps the LCP image visible from first paint
  tl.from(image, { scale: 1.14, duration: 1.6, ease: 'power2.out' }, 0)
    .from(scrim, { opacity: 0, duration: 1.2, ease: 'power1.out' }, 0.1)
    .from(eyebrow, { opacity: 0, y: 16, duration: 0.7 }, 0.45)
    .from(lead, { opacity: 0, y: 22, duration: 0.8 }, 0.9)
    .from(cta, { opacity: 0, y: 20, duration: 0.7, stagger: 0.1 }, 1.0)
    .from(meta, { opacity: 0, y: 16, duration: 0.6, stagger: 0.08 }, 1.1)
    .from(cue, { opacity: 0, y: -10, duration: 0.6 }, 1.2)

  return tl
}
